import { baseResponseSchema, BaseResponseSchemaType } from '../../../schemas/repository.schemas';
import {
    ExecutionConfigType,
    listTasksFiltersSchema,
    ListTasksFiltersType,
    taskCreationSchema,
    TaskCreationSchemaType,
    taskExecutionCreationSchema,
    TaskExecutionCreationSchemaType,
    TaskExecutionIdType,
    taskExecutionsSetSchema,
    taskExecutionUpdateSchema,
    TaskExecutionUpdateSchemaType,
    TaskIdType,
    TaskStatusType,
    taskUpdateSchema,
    TaskUpdateSchemaType,
} from '../../../schemas/task.schemas';
import HttpReq from '../../../service/httpReq';
import {
    taskExecutionDetailsResponseSchema,
    TaskExecutionDetailsResponseSchemaType,
    taskExecutionInfoListResponseSchema,
    TaskExecutionInfoListResponseSchemaType,
    taskExecutionInfoResponseSchema,
    TaskExecutionInfoResponseSchemaType,
    taskInfoListResponseSchema,
    TaskInfoListResponseSchemaType,
    taskResponseSchema,
    TaskResponseSchemaType,
} from './schemas';

class TasksRepo {
    static publicPath = 'api/mgmt/tasks';

    static listTasks(
        filterSpecs: ListTasksFiltersType,
        successConsumer: (response: TaskInfoListResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        listTasksFiltersSchema.parse(filterSpecs);

        const searchParams = new URLSearchParams();
        if (filterSpecs.statuses) {
            filterSpecs.statuses.forEach((status) => searchParams.append('statuses', status));
        }
        if (filterSpecs.assignedUserId) {
            searchParams.append('assignedUserId', filterSpecs.assignedUserId.toString());
        }
        searchParams.append('page', filterSpecs.page.toString());

        HttpReq.get(
            `${TasksRepo.publicPath}?${searchParams.toString()}`,
            (response: unknown) => {
                console.log(taskInfoListResponseSchema.safeParse(response));
                successConsumer(taskInfoListResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static createTask(
        createTasksSpecs: TaskCreationSchemaType,
        successConsumer: (response: TaskResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        // Validate the createTasksSpecs against the schema before making the API call
        // This will throw an error if the validation fails, which can be caught by the errorConsumer
        taskCreationSchema.parse(createTasksSpecs);
        HttpReq.post(
            TasksRepo.publicPath,
            createTasksSpecs,
            (response: unknown) => {
                successConsumer(taskResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static getTask(
        taskId: TaskIdType,
        successConsumer: (response: TaskResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        HttpReq.get(
            `${TasksRepo.publicPath}/${taskId}`,
            (response: unknown) => {
                successConsumer(taskResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static updateTask(
        taskId: TaskIdType,
        updateTaskSpecs: TaskUpdateSchemaType,
        successConsumer: (response: TaskResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        taskUpdateSchema.parse(updateTaskSpecs);
        HttpReq.put(
            `${TasksRepo.publicPath}/${taskId}`,
            updateTaskSpecs,
            (response: unknown) => {
                successConsumer(taskResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static deleteTask(
        taskId: TaskIdType,
        successConsumer: (response: BaseResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        HttpReq.delete(
            `${TasksRepo.publicPath}/${taskId}`,
            (response: unknown) => {
                successConsumer(baseResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static getTaskExecutions(
        taskId: TaskIdType,
        page: number,
        successConsumer: (response: TaskExecutionInfoListResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        const searchParams = new URLSearchParams({
            page: page.toString(),
        });

        HttpReq.get(
            `${TasksRepo.publicPath}/${taskId}/executions?${searchParams.toString()}`,
            (response: unknown) => {
                successConsumer(taskExecutionInfoListResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static setTaskExecutions(
        taskId: TaskIdType,
        executionConfigs: ExecutionConfigType[],
        successConsumer: (response: TaskExecutionInfoListResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        const payload = { taskId, executionConfigs };
        taskExecutionsSetSchema.parse(payload);
        HttpReq.put(
            `${TasksRepo.publicPath}/${taskId}/executions`,
            payload,
            (response: unknown) => {
                successConsumer(taskExecutionInfoListResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static addTaskExecution(
        taskId: TaskIdType,
        executionConfig: TaskExecutionCreationSchemaType,
        successConsumer: (response: TaskExecutionInfoResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        taskExecutionCreationSchema.parse(executionConfig);
        HttpReq.post(
            `${TasksRepo.publicPath}/${taskId}/executions`,
            executionConfig,
            (response: unknown) => {
                successConsumer(taskExecutionInfoResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static updateTaskExecution(
        taskId: TaskIdType,
        execution: TaskExecutionUpdateSchemaType,
        successConsumer: (response: TaskExecutionInfoResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        taskExecutionUpdateSchema.parse(execution);
        HttpReq.put(
            `${TasksRepo.publicPath}/${taskId}/executions/${execution.executionId}`,
            execution,
            (response: unknown) => {
                successConsumer(taskExecutionInfoResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static deleteTaskExecution(
        taskId: TaskIdType,
        executionId: TaskExecutionIdType,
        successConsumer: (response: BaseResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        HttpReq.delete(
            `${TasksRepo.publicPath}/${taskId}/executions/${executionId}`,
            (response: unknown) => {
                successConsumer(baseResponseSchema.parse(response));
            },
            errorConsumer
        );
    }

    static getTaskExecutionsByEntity(
        entityId: number,
        page: number,
        statuses: TaskStatusType[],
        successConsumer: (response: TaskExecutionDetailsResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void
    ) {
        const searchParams = new URLSearchParams({
            include: 'task',
            entityId: entityId.toString(),
            page: page.toString(),
        });

        statuses.forEach((status) => searchParams.append('statuses', status));

        HttpReq.get(
            `${TasksRepo.publicPath}/executions?${searchParams.toString()}`,
            (response: unknown) => {
                successConsumer(taskExecutionDetailsResponseSchema.parse(response));
            },
            errorConsumer
        );
    }
}

export default TasksRepo;
